import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { API_URL } from '../../constants';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { Button } from '../../components/library';
import HoverableImageThumbnail from '../catalog/productImages/HoverableImageThumbnail';
import { FormatDateString } from '../../utils/helper';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { WarehouseBinAutocomplete } from '../../components/inventory/WarehouseBinAutocomplete';
import { WarehouseBin } from '../../services/inventory/types';
import {
  FulfillmentBinOption,
  FulfillmentLifecycleStatus,
  FulfillmentLineExceptionType,
  FulfillmentLineStatus,
  LineItem,
  LineItemLocalState,
  OrderDetail,
} from '../../services/fulfillment/types';
import {
  EXCEPTION_TYPES,
  LIFECYCLE_STATUSES,
  LINE_STATUSES,
} from '../../services/fulfillment/constants';
import { groupBinsByZone } from '../../services/fulfillment/utils';
import WaveStatusChip from './WaveStatusChip';
import RefurbishedItemRelocator from '../inventory/relocate/ReburbishedItemRelocator';

export const FULFILLMENT_ORDER_DETAIL_ROUTE = '/fulfillment/orders/:shopifyOrderId';

const GRID_COLUMNS = '60px 1fr 70px 210px 240px';
const MAX_WIDTH = '960px';

// ─── Component ───────────────────────────────────────────────────────────────

const FulfillmentOrderDetail: React.FC = () => {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const history = useHistory();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fulfillmentBins, setFulfillmentBins] = useState<FulfillmentBinOption[]>([]);
  const [orderStatus, setOrderStatus] = useState<FulfillmentLifecycleStatus | null>(null);
  const [lineStatuses, setLineStatuses] = useState<Record<string, LineItemLocalState>>({});
  const [savingOrderStatus, setSavingOrderStatus] = useState(false);
  const [savingLineGid, setSavingLineGid] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [orderStatusWarning, setOrderStatusWarning] = useState<string | null>(null);
  const [lineBinWarnings, setLineBinWarnings] = useState<Record<string, string>>({});
  const [inventoryDialogItem, setInventoryDialogItem] = useState<LineItem | null>(null);
  const [relocatorSku, setRelocatorSku] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const [orderRes, binsRes] = await Promise.all([
          axios.get(`${API_URL}/fulfillment/orders/${shopifyOrderId}`),
          axios.get(`${API_URL}/fulfillment/bins`),
        ]);
        const orderData: OrderDetail = (orderRes as any).data;
        setFulfillmentBins((binsRes as any).data ?? []);
        setOrderStatus((orderData.lifecycleStatus as FulfillmentLifecycleStatus) ?? null);

        const initialStatuses: Record<string, LineItemLocalState> = {};
        for (const li of orderData.lineItems) {
          initialStatuses[li.shopifyLineItemGid] = {
            status: li.lineStatus ?? 'pending',
            fulfillmentBinCode: li.fulfillmentBinCode,
            exceptionType: li.exceptionType,
            notes: li.exceptionNotes,
            pendingStatus: null,
            pendingBin: null,
            pendingBinTypeahead: '',
            pendingExceptionType: null,
            pendingNotes: null,
          };
        }
        setLineStatuses(initialStatuses);
        setOrder(orderData);
      } catch {
        setError('Failed to load order details.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [shopifyOrderId, refreshKey]);

  const handleOrderStatusChange = async (newStatus: FulfillmentLifecycleStatus) => {
    const prev = orderStatus;
    setOrderStatus(newStatus);
    setSavingOrderStatus(true);
    try {
      const res = await axios.patch(`${API_URL}/fulfillment/orders/${shopifyOrderId}/status`, {
        status: newStatus,
      });
      if ((res as any).data?.warning) setOrderStatusWarning((res as any).data.warning);
    } catch {
      setOrderStatus(prev);
      setApiError('Failed to update order status');
    } finally {
      setSavingOrderStatus(false);
    }
  };

  const saveLineStatus = async (
    gid: string,
    payload: {
      status: FulfillmentLineStatus;
      fulfillmentBinCode?: string;
      exceptionType?: FulfillmentLineExceptionType;
      notes?: string;
    },
  ) => {
    const prev = lineStatuses[gid];
    setSavingLineGid(gid);
    setLineBinWarnings(w => ({ ...w, [gid]: '' }));
    try {
      const res = await axios.patch(
        `${API_URL}/fulfillment/orders/${shopifyOrderId}/lines/status`,
        {
          lineItemGid: gid,
          ...payload,
        },
      );
      const warning = (res as any).data?.warning;
      setLineStatuses(s => ({
        ...s,
        [gid]: {
          status: payload.status,
          fulfillmentBinCode: payload.fulfillmentBinCode ?? null,
          exceptionType: payload.exceptionType ?? null,
          notes: payload.notes ?? null,
          pendingStatus: null,
          pendingBin: null,
          pendingBinTypeahead: '',
          pendingExceptionType: null,
          pendingNotes: null,
        },
      }));
      if (warning) setLineBinWarnings(w => ({ ...w, [gid]: warning }));
    } catch {
      setLineStatuses(s => ({ ...s, [gid]: prev }));
      setApiError('Failed to update line item status');
    } finally {
      setSavingLineGid(null);
    }
  };

  const handleLineStatusSelectChange = (gid: string, newStatus: FulfillmentLineStatus) => {
    if (newStatus === 'pending') {
      // Immediate save — no secondary controls needed
      saveLineStatus(gid, { status: newStatus });
    } else {
      // Deferred: show secondary controls
      setLineStatuses(prev => ({
        ...prev,
        [gid]: {
          ...prev[gid],
          pendingStatus: newStatus,
          pendingBin: null,
          pendingBinTypeahead: '',
          pendingExceptionType: null,
          pendingNotes: null,
        },
      }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
        <Box padding="4rem">
          <Alert severity="error">{error ?? 'Order not found.'}</Alert>
        </Box>
      </Box>
    );
  }

  const customerName = order.customer
    ? `${order.customer.firstName} ${order.customer.lastName}`.trim()
    : null;

  const addressParts = order.shippingAddress
    ? [
        order.shippingAddress.address1,
        order.shippingAddress.address2,
        `${order.shippingAddress.city}, ${order.shippingAddress.province} ${order.shippingAddress.zip}`,
      ].filter(Boolean)
    : [];

  const inWave = order.waveStatus === 'in_wave';

  const activeLines = order.lineItems.filter(
    li => (lineStatuses[li.shopifyLineItemGid]?.status ?? li.lineStatus) !== 'removed',
  );
  const removedLines = order.lineItems.filter(li => li.lineStatus === 'removed');

  const renderLocationCell = (item: LineItem) => {
    const ls = lineStatuses[item.shopifyLineItemGid];
    if (!ls) return <Typography sx={{ fontSize: 13, color: 'grey.500' }}>—</Typography>;

    const effectiveStatus = ls.pendingStatus ?? ls.status;

    // For picked/packed and beyond, show fulfilled location
    if (effectiveStatus === 'picked' || effectiveStatus === 'packed') {
      const binCode = ls.pendingBin?.code ?? ls.fulfillmentBinCode;
      return (
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
          {binCode ?? '—'}
        </Typography>
      );
    }

    if (effectiveStatus !== 'exception') {
      // For quality-check, filter for qc bins and optionally refurbished bins
      let filterLocations: string[] = [];
      if (effectiveStatus === 'quality-check') {
        filterLocations = ['qc'];
        if (order?.qcAllowRefurbishedZone) {
          filterLocations.push('refurbished');
        }
      } else {
        filterLocations = ['pick'];
      }

      const zoneGroups = groupBinsByZone(
        item.binLocations.filter(b => filterLocations.includes(b.locationType as any)),
      );
      if (zoneGroups.length === 0)
        return (
          <Typography sx={{ fontSize: 13, color: 'grey.500' }}>Not in Pickable location</Typography>
        );
      return (
        <>
          {zoneGroups.map(group => (
            <Box key={group.zoneLabel} sx={{ mb: group.bins.length > 0 ? 1 : 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'grey.600', mb: 0.25 }}>
                {group.zoneLabel}
              </Typography>
              {group.bins.map(bin => (
                <Box
                  key={bin.binCode}
                  sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', pl: 1 }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
                    {bin.binCode}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'grey.500' }}>
                    qty {bin.quantity}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </>
      );
    }

    if (effectiveStatus === 'exception') {
      const excType = ls.pendingStatus ? ls.pendingExceptionType : ls.exceptionType;
      const notes = ls.pendingStatus ? ls.pendingNotes : ls.notes;
      const label = EXCEPTION_TYPES.find(e => e.value === excType)?.label ?? excType ?? '—';
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'error.main' }}>
            {label}
          </Typography>
          {notes && <Typography sx={{ fontSize: 12, color: 'grey.600' }}>{notes}</Typography>}
        </Box>
      );
    }

    return <Typography sx={{ fontSize: 13, color: 'grey.500' }}>—</Typography>;
  };

  const renderStatusCell = (item: LineItem) => {
    const ls = lineStatuses[item.shopifyLineItemGid];
    if (!ls) return null;

    const isRemoved = ls.status === 'removed';
    if (isRemoved) {
      return (
        <Chip
          label="Removed"
          size="small"
          sx={{ color: 'grey.500', borderColor: 'grey.400' }}
          variant="outlined"
        />
      );
    }

    const selectedStatus = ls.pendingStatus ?? ls.status;
    const saving = savingLineGid === item.shopifyLineItemGid;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Autocomplete<FulfillmentLineStatus>
          options={LINE_STATUSES}
          value={selectedStatus}
          onChange={(_, newVal) => {
            if (newVal) handleLineStatusSelectChange(item.shopifyLineItemGid, newVal);
          }}
          getOptionLabel={s => s}
          getOptionSelected={(opt, val) => opt === val}
          disabled={inWave || saving}
          renderInput={params => <TextField {...params} variant="outlined" size="small" />}
        />

        {/* Secondary controls for quality-check */}
        {ls.pendingStatus === 'quality-check' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <WarehouseBinAutocomplete
              options={item.binLocations
                .filter(b => b.locationType === 'qc')
                .map(b => (({ id: b.id, code: b.binCode } as unknown) as WarehouseBin))}
              selectedBin={
                ls.pendingBin
                  ? (({
                      id: ls.pendingBin.id,
                      code: ls.pendingBin.code,
                    } as unknown) as WarehouseBin)
                  : undefined
              }
              typeaheadVal={ls.pendingBinTypeahead}
              onTypeaheadChange={(val: string) =>
                setLineStatuses(prev => ({
                  ...prev,
                  [item.shopifyLineItemGid]: {
                    ...prev[item.shopifyLineItemGid],
                    pendingBinTypeahead: val,
                  },
                }))
              }
              onBinChange={(bin: WarehouseBin | undefined) =>
                setLineStatuses(prev => ({
                  ...prev,
                  [item.shopifyLineItemGid]: {
                    ...prev[item.shopifyLineItemGid],
                    pendingBin: bin ? { id: bin.id, code: bin.code } : null,
                  },
                }))
              }
            />
            <Button
              ordinality="primary"
              size="small"
              disabled={!ls.pendingBin || saving}
              onClick={() => {
                if (ls.pendingBin) {
                  saveLineStatus(item.shopifyLineItemGid, {
                    status: 'quality-check',
                    fulfillmentBinCode: ls.pendingBin.code,
                  });
                }
              }}
            >
              Save
            </Button>
          </Box>
        )}

        {/* Secondary controls for picked/packed */}
        {ls.pendingStatus && (ls.pendingStatus === 'picked' || ls.pendingStatus === 'packed') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <WarehouseBinAutocomplete
              options={fulfillmentBins.map(
                b => (({ id: b.id, code: b.binCode } as unknown) as WarehouseBin),
              )}
              selectedBin={
                ls.pendingBin
                  ? (({
                      id: ls.pendingBin.id,
                      code: ls.pendingBin.code,
                    } as unknown) as WarehouseBin)
                  : undefined
              }
              typeaheadVal={ls.pendingBinTypeahead}
              onTypeaheadChange={(val: string) =>
                setLineStatuses(prev => ({
                  ...prev,
                  [item.shopifyLineItemGid]: {
                    ...prev[item.shopifyLineItemGid],
                    pendingBinTypeahead: val,
                  },
                }))
              }
              onBinChange={(bin: WarehouseBin | undefined) =>
                setLineStatuses(prev => ({
                  ...prev,
                  [item.shopifyLineItemGid]: {
                    ...prev[item.shopifyLineItemGid],
                    pendingBin: bin ? { id: bin.id, code: bin.code } : null,
                  },
                }))
              }
            />
            <Button
              ordinality="primary"
              size="small"
              disabled={!ls.pendingBin || saving}
              onClick={() => {
                if (ls.pendingStatus && ls.pendingBin) {
                  saveLineStatus(item.shopifyLineItemGid, {
                    status: ls.pendingStatus,
                    fulfillmentBinCode: ls.pendingBin.code,
                  });
                }
              }}
            >
              Save
            </Button>
          </Box>
        )}

        {/* Secondary controls for exception */}
        {ls.pendingStatus === 'exception' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Autocomplete<{ value: FulfillmentLineExceptionType; label: string }>
              options={EXCEPTION_TYPES}
              value={EXCEPTION_TYPES.find(e => e.value === ls.pendingExceptionType) ?? null}
              onChange={(_, newVal) =>
                setLineStatuses(prev => ({
                  ...prev,
                  [item.shopifyLineItemGid]: {
                    ...prev[item.shopifyLineItemGid],
                    pendingExceptionType: newVal?.value ?? null,
                  },
                }))
              }
              getOptionLabel={et => et.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              disabled={saving}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Exception type…"
                />
              )}
            />
            <TextField
              variant="outlined"
              size="small"
              placeholder="Notes (optional)"
              value={ls.pendingNotes ?? ''}
              disabled={saving}
              onChange={e =>
                setLineStatuses(prev => ({
                  ...prev,
                  [item.shopifyLineItemGid]: {
                    ...prev[item.shopifyLineItemGid],
                    pendingNotes: e.target.value || null,
                  },
                }))
              }
              inputProps={{ style: { fontSize: 13 } }}
            />
            <Button
              ordinality="primary"
              size="small"
              disabled={!ls.pendingExceptionType || saving}
              onClick={() => {
                if (ls.pendingExceptionType) {
                  saveLineStatus(item.shopifyLineItemGid, {
                    status: 'exception',
                    exceptionType: ls.pendingExceptionType,
                    notes: ls.pendingNotes ?? undefined,
                  });
                }
              }}
            >
              Save
            </Button>
          </Box>
        )}

        {lineBinWarnings[item.shopifyLineItemGid] && (
          <Alert
            severity="warning"
            sx={{ fontSize: 12, py: 0 }}
            onClose={() => setLineBinWarnings(w => ({ ...w, [item.shopifyLineItemGid]: '' }))}
          >
            {lineBinWarnings[item.shopifyLineItemGid]}
          </Alert>
        )}
      </Box>
    );
  };

  const renderLineRow = (item: LineItem, isRemoved: boolean) => {
    const ls = lineStatuses[item.shopifyLineItemGid];
    return (
      <Box
        key={item.shopifyLineItemGid}
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          gap: 2,
          alignItems: 'start',
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          maxWidth: MAX_WIDTH,
          ...(isRemoved && { bgcolor: 'grey.100', opacity: 0.75 }),
        }}
      >
        {/* Thumbnail */}
        <HoverableImageThumbnail
          src={item.imageUrl ?? undefined}
          identifier={item.sku ?? undefined}
        />

        {/* Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.5px' }}>
              {item.sku ?? '—'}
            </Typography>
            {item.binLocations.length > 0 && (
              <Tooltip title="View inventory locations">
                <IconButton
                  size="small"
                  onClick={() => setInventoryDialogItem(item)}
                  sx={{ color: 'grey.500', '&:hover': { color: 'primary.main' } }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18px"
                    height="18px"
                    viewBox="0 -960 960 960"
                    fill="#ccc"
                  >
                    <path d="M120-40v-880h80v80h560v-80h80v880h-80v-80H200v80h-80Zm80-480h80v-160h240v160h240v-240H200v240Zm0 320h240v-160h240v160h80v-240H200v240Zm160-320h80v-80h-80v80Zm160 320h80v-80h-80v80ZM360-520h80-80Zm160 320h80-80Z" />
                  </svg>
                </IconButton>
              </Tooltip>
            )}
            {item.isRefurbished && item.sku && (
              <Tooltip title="Relocate item">
                <IconButton
                  size="small"
                  onClick={() => setRelocatorSku(item.sku)}
                  sx={{ color: 'grey.400', '&:hover': { color: 'warning.main' } }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {item.title && (
            <Typography sx={{ fontSize: 13, color: 'grey.600' }}>{item.title}</Typography>
          )}
          {item.upc && (
            <Typography sx={{ fontSize: 11, mt: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
                UPC
              </Box>
              <Box component="span" sx={{ color: 'grey.500' }}>
                {item.upc}
              </Box>
            </Typography>
          )}
          {item.mpn && (
            <Typography sx={{ fontSize: 11 }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
                MPN
              </Box>
              <Box component="span" sx={{ color: 'grey.500' }}>
                {item.mpn}
              </Box>
            </Typography>
          )}
        </Box>

        {/* Order Qty */}
        <Typography sx={{ fontSize: 14, color: 'grey.700', whiteSpace: 'nowrap' }}>
          {item.quantity != null ? `×${item.quantity}` : '—'}
        </Typography>

        {/* Status */}
        <Box>{renderStatusCell(item)}</Box>

        {/* Location */}
        <Box>
          {isRemoved && !ls ? (
            <Typography sx={{ fontSize: 13, color: 'grey.500' }}>-</Typography>
          ) : (
            renderLocationCell(item)
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
      {/* Back bar */}
      <Box sx={{ padding: '1.5rem 4rem 0' }}>
        <Button
          ordinality="secondary"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/fulfillment/orders')}
        >
          Fulfillment Orders
        </Button>
      </Box>

      {/* Two-column body */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '280px 1fr',
          minHeight: 0,
          flex: 1,
        }}
      >
        {/* ── Left: order header panel ── */}
        <Box
          sx={{
            borderRight: '1px solid',
            borderColor: 'grey.300',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 300, mb: 0 }}>Shopify Order</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '1px', pt: 0 }}>
              <a
                href={`https://admin.shopify.com/store/${order.shopifyStoreName}/orders/${order.shopifyOrderId}`}
                target="_blank"
                rel="noreferrer"
              >
                {order.shopifyOrderName}
              </a>
              <OpenInNew fontSize="small" color="disabled" />
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            <WaveStatusChip status={order.waveStatus} waveId={order.waveId} />
            <Chip label={order.orderType || 'Unknown'} size="small" />
            {order.hasDropShip && <Chip label="Drop Ship" size="small" />}
          </Box>

          <Box
            component="hr"
            sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.200', m: 0 }}
          />

          {/* Fulfillment status */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: 'grey.600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Fulfillment Status
            </Typography>
            <Autocomplete<FulfillmentLifecycleStatus>
              options={LIFECYCLE_STATUSES}
              value={orderStatus}
              onChange={(_, newVal) => {
                if (newVal) handleOrderStatusChange(newVal);
              }}
              getOptionLabel={s => s}
              getOptionSelected={(opt, val) => opt === val}
              disabled={inWave || savingOrderStatus}
              renderInput={params => (
                <TextField {...params} variant="outlined" size="small" placeholder="—" />
              )}
            />
            {orderStatusWarning && (
              <Alert
                severity="warning"
                sx={{ fontSize: 12 }}
                onClose={() => setOrderStatusWarning(null)}
              >
                {orderStatusWarning}
              </Alert>
            )}
          </Box>

          <Box
            component="hr"
            sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.200', m: 0 }}
          />

          {customerName && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'grey.600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Customer
              </Typography>
              <Typography sx={{ fontSize: 14 }}>{customerName}</Typography>
              {order.customer?.email && (
                <Typography sx={{ fontSize: 14 }}>{order.customer.email}</Typography>
              )}
            </Box>
          )}

          {addressParts.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'grey.600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Ship to
              </Typography>
              {addressParts.map((line, i) => (
                <Typography key={i} sx={{ fontSize: 14 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: 'grey.600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Order date
            </Typography>
            <Typography sx={{ fontSize: 14 }}>{FormatDateString(order.createdAt)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: 'grey.600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Items
            </Typography>
            <Typography sx={{ fontSize: 14 }}>{activeLines.length}</Typography>
          </Box>
        </Box>

        {/* ── Right: line items ── */}
        <Box sx={{ p: '3rem 4rem' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 3 }}>Line Items</Typography>

          {/* Header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              gap: 2,
              pb: 1,
              borderBottom: '2px solid',
              borderColor: 'grey.300',
              maxWidth: MAX_WIDTH,
              mb: 0.5,
            }}
          >
            <div />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Details
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Order Qty
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Status
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Location
            </Typography>
          </Box>

          {/* Active line item rows */}
          {activeLines.map(item => renderLineRow(item, false))}

          {/* Removed lines section */}
          {removedLines.length > 0 && (
            <>
              <Box
                sx={{
                  maxWidth: MAX_WIDTH,
                  mt: 3,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.300' }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: 'grey.500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Items removed from Shopify order
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.300' }} />
              </Box>
              {removedLines.map(item => renderLineRow(item, true))}
            </>
          )}
        </Box>
      </Box>

      {/* Inventory locations dialog */}
      <Dialog
        open={!!inventoryDialogItem}
        onClose={() => setInventoryDialogItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Inventory Locations
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
              {inventoryDialogItem?.sku ?? '—'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setInventoryDialogItem(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {inventoryDialogItem &&
            (() => {
              const zoneGroups = groupBinsByZone(inventoryDialogItem.binLocations);
              if (zoneGroups.length === 0) {
                return (
                  <Typography sx={{ fontSize: 13, color: 'grey.500', py: 1 }}>
                    No inventory locations found.
                  </Typography>
                );
              }
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                  {zoneGroups.map(group => (
                    <Box key={group.zoneLabel}>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'grey.500',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 0.75,
                        }}
                      >
                        {group.zoneLabel}
                      </Typography>
                      {group.bins.map(bin => (
                        <Box
                          key={bin.binCode}
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            py: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'grey.100',
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
                            {bin.binCode}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: 'grey.500' }}>
                            qty {bin.quantity}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              );
            })()}
        </DialogContent>
      </Dialog>

      {/* Relocator dialog */}
      <Dialog open={!!relocatorSku} onClose={() => setRelocatorSku(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          <IconButton
            size="small"
            onClick={() => setRelocatorSku(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {relocatorSku && (
            <RefurbishedItemRelocator
              sku={relocatorSku}
              onSuccess={() => {
                setRelocatorSku(null);
                setRefreshKey(k => k + 1);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Error snackbar */}
      <Snackbar
        open={!!apiError}
        autoHideDuration={5000}
        onClose={() => setApiError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setApiError(null)}>
          {apiError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FulfillmentOrderDetail;
